import type { VolumeStorageLabels } from "../blobstoreStorageLabels";

export const VOLUME_NAME_RE = /^[a-z0-9][a-z0-9_-]{0,62}$/;

export type VolumeScope = "branch" | "run";
export type VolumeAccess = "rw" | "ro";
export type VolumeConcurrency = "shared" | "exclusive";

export type VolumeDirective = {
  name: string;
  scope: VolumeScope;
  access: VolumeAccess;
  concurrency: VolumeConcurrency;
};

// Volumes a draft has not been seeded with, by the branch holding their data.
export type UndecidedDraftVolumes = {
  live: string[];
  forkSource: string[];
};

type VolumeRefKind = "branch" | "expiring" | "run" | "user";

type VolumeIdentityBase = {
  namespace: string;
  volumeId: string;
  refId: string;
};

export type VolumeIdentity = VolumeIdentityBase & {
  name: string;
  refKind: VolumeRefKind;
};

export type WritableVolumeIdentity =
  | (VolumeIdentityBase & {
      name: string;
      refKind: "branch";
      storageLabels: Extract<
        VolumeStorageLabels,
        { kind: "teamBranch" | "stepTestBranch" }
      >;
    })
  | (VolumeIdentityBase & {
      name: string;
      refKind: "expiring";
      storageLabels: Extract<
        VolumeStorageLabels,
        { kind: "stepTestBranch" }
      > & {
        workflowId: string;
        branchId: string;
        stepDir: string;
      };
      retentionSeconds: number;
    })
  | (VolumeIdentityBase & {
      name: string;
      refKind: "run";
      storageLabels: Extract<VolumeStorageLabels, { kind: "run" }>;
    })
  | (VolumeIdentityBase & {
      name: string;
      refKind: "user";
      storageLabels: Extract<VolumeStorageLabels, { kind: "personal" }>;
    });

export type VolumeMount = VolumeDirective &
  WritableVolumeIdentity & {
    path?: string;
  };

export type FileWriteVolumeIdentity = Exclude<
  WritableVolumeIdentity,
  { refKind: "run" | "expiring" }
>;

export function validateWritableVolumeIdentity(
  identity: WritableVolumeIdentity
): void {
  switch (identity.refKind) {
    case "run":
      if (identity.storageLabels.runId !== identity.refId) {
        throw new Error("Run storage label does not match the volume ref");
      }
      break;
    case "user":
      if (identity.storageLabels.userId !== identity.refId) {
        throw new Error("Personal storage label does not match the volume ref");
      }
      break;
    case "branch":
      if (
        identity.storageLabels.kind === "teamBranch" &&
        identity.storageLabels.branchId !== undefined &&
        identity.storageLabels.branchId !== identity.refId
      ) {
        throw new Error("Branch storage label does not match the volume ref");
      }
      break;
  }
}

export function volumeIsReadOnly(volume: VolumeDirective): boolean {
  return volume.access === "ro";
}

export type VolumeEntry = {
  name: string;
  kind: "file" | "directory" | "symlink";
  size: number;
  modifiedAt: number;
};

export type VolumeUsage = {
  logicalBytes: number;
};

export type ListOptions = {
  path?: string;
  cursor?: string;
  limit?: number;
};

export type ListResult = {
  entries: VolumeEntry[];
  nextCursor?: string;
};

export type ReadResult = {
  body: Uint8Array<ArrayBuffer>;
  size: number;
  range?: {
    start: number;
    end: number;
    total: number;
  };
};

export type ReadOptions = {
  rangeHeader?: string | null;
};

export class VolumeFileNotFound extends Error {
  constructor(path: string) {
    super(`File not found: ${path}`);
    this.name = "VolumeFileNotFound";
  }
}

export class VolumeFileTooLarge extends Error {
  constructor(path: string) {
    super(`File is too large to read: ${path}`);
    this.name = "VolumeFileTooLarge";
  }
}

export class VolumeNotAFile extends Error {
  constructor(path: string) {
    super(`Path is not a file: ${path}`);
    this.name = "VolumeNotAFile";
  }
}

export class VolumeRangeNotSatisfiable extends Error {
  constructor(readonly contentRange?: string) {
    super("Range not satisfiable");
    this.name = "VolumeRangeNotSatisfiable";
  }
}

export type VolumeForkResult = "forked" | "destination_exists";

export interface VolumeBackend {
  list(id: VolumeIdentity, opts?: ListOptions): Promise<ListResult>;
  usage(id: VolumeIdentity): Promise<VolumeUsage>;
  read(
    id: VolumeIdentity,
    path: string,
    opts?: ReadOptions
  ): Promise<ReadResult>;
  fork(
    source: VolumeIdentity,
    destination: WritableVolumeIdentity & { refKind: "branch" }
  ): Promise<VolumeForkResult>;
  remove(id: VolumeIdentity): Promise<void>;
  write(
    id: FileWriteVolumeIdentity,
    path: string,
    body: Uint8Array<ArrayBuffer>
  ): Promise<void>;
  removePath(id: FileWriteVolumeIdentity, path: string): Promise<void>;
}
